import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { post } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';
import { showToast } from 'data/reducers/toastReducer';

/**
 * "Report wrong image" control shared by the Current-image widget and its
 * enlarged modal -- both need the same report call, loading state, and
 * reported-once-per-view behavior, so this is the one place that does it.
 *
 * Renders nothing for a logged-out viewer or when no propertyId is given
 * (the modal passes null for historic/luna photos, which aren't Street View).
 */
const ReportStreetViewImageButton = ({ propertyId, iconOnly }) => {
	const dispatch = useDispatch();
	const authToken = useSelector((state) => state.user.authToken);
	const isLoggedIn = useSelector((state) => !!state.user.userData?.id);
	const [isReporting, setIsReporting] = useState(false);
	const [reported, setReported] = useState(false);

	if (!isLoggedIn || !propertyId) {
		return null;
	}

	const handleReport = () => {
		setIsReporting(true);
		post({
			url: `${getApiUrl()}/api/properties/${propertyId}/street-view-report`,
			authToken,
			dispatch,
		})
			.then(() => {
				setReported(true);
				dispatch(
					showToast({
						title: 'Street View',
						body: "Thanks, we've flagged this image for review.",
						type: 'success',
					}),
				);
			})
			.catch((error) => {
				dispatch(
					showToast({
						title: 'Street View',
						body: error?.message || 'Could not submit the report.',
						type: 'danger',
					}),
				);
			})
			.finally(() => {
				setIsReporting(false);
			});
	};

	if (iconOnly) {
		return (
			<button
				type="button"
				disabled={isReporting || reported}
				onClick={handleReport}
				title={reported ? 'Reported' : 'Report wrong image'}
				aria-label={reported ? 'Reported' : 'Report wrong image'}
			>
				<i className={`bi ${reported ? 'bi-flag-fill' : 'bi-flag'}`} />
			</button>
		);
	}

	return (
		<button
			type="button"
			className="btn btn-outline-danger"
			disabled={isReporting || reported}
			onClick={handleReport}
		>
			{reported ? 'Reported' : isReporting ? 'Reporting…' : 'Report wrong image'}
		</button>
	);
};

export default ReportStreetViewImageButton;
